// React Components
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

// Material UI Components
import { Button, Grid, Paper } from '@mui/material';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import Box from '@mui/material/Box';

// Audit Vault Components
import AccessDenied from "../components/common/AccessDenied";
import ServiceIsDown from "../components/common/ServiceIsDown";
import ProgressBar from "../components/common/ProgressBar";
import CustomerPageScaffold from "../components/common/CustomerPageScaffold";
import TenantDBStats from "../components/TenantDBStats";
import AuditsByWorkloadSource from "../components/reports/AuditsByWorkloadSource";
import AuditGrowth from "../components/reports/AuditGrowth";
import TenantSetupRequired from "../components/tenant/TenantSetupRequired";
import InsightsSearchBox from "../components/reports/InsightsSearchBox";
import ReportsByWorkloadBox from "../components/reports/ReportsByWorkloadBox";

// Audit Vault Utilities
import { getCompanyByCompanyId } from "../utilities/common-company";
import { REPORT_ON_PERIOD_1WEEK } from "../constants/constants-reportperiods";
import { verifyUserHasTenantRolePermissions } from "../utilities/common-user-utils";
import { ROLE_CODE_TENANTREPORTREADER } from "../constants/constants-roles";
import { getHealthStatsByTenantId } from "../utilities/common-healthstats";

/*
Displays an executive dashboard summary for Company X.
Takes in companyId as optional parameter.
*/
function Reports(props) {

    const { user, openCompanyNag, setOpenCompanyNag } = props;

    // Component Constants
    const [company, setCompany] = useState("");
    const [tenantHealthStats, setTenantHealthStats] = useState("");
    const [selectedTenantOption, setSelectedTenantOption] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);

    const [totalAuditRecords, setTotalAuditRecords] = useState(null);
    const [totalSPRecords, setTotalSPRecords] = useState(null);
    const [totalExchangeRecords, setTotalExchangeRecords] = useState(null);
    const [totalEntraRecords, setTotalEntraRecords] = useState(null);
    const [totalTeamsRecords, setTotalTeamsRecords] = useState(null);
    const [loadingTotalAuditRecords, setLoadingTotalAuditRecords] = useState(false);
    const [totalAuditRecordsLoaded, setTotalAuditRecordsLoaded] = useState(false);

    const [loading, setLoading] = useState(true);
    const [serviceIsDownError, setServiceIsDownError] = useState(false);
    const navigate = useNavigate();

    const theBreadcrumbPath = [
        { name: 'Home', link: '/' },
        { name: 'Reports', link: '/Reports/ReportsHome' },
    ];

    const theSectionTitle = "Audit Vault for M365 - Executive Reports Dashboard";
    const theSectionDesc = "Your M365 audit logs are safely being managed by Audit Vault for M365.  This allows you to ensure that pro-active monitoring, compliance and security are enabled for your organization’s data.  You may perform passive monitoring, active event monitoring, track user and activity, perform security audits, and preserve and retrieve a complete history for documents and user activity to ensure complete compliance.";
    const isAdminPage = false;



    const handleTenantOptionChange = (event) => {
        //setSelectedTenantOption(event.target.value);
    };

    // Component Functions
    async function fetchTenantHealthStats(tenantId) {
        setLoading(true);
        try {
            setAccessDenied(false);
            // We attempt to load the tenant health stats (which lets us see if the tenant is pending still etc).
            const response2 = await getHealthStatsByTenantId(tenantId, 1, user);
            if (response2) {
                setTenantHealthStats(response2);
            }
        }
        catch (e) {
            console.log("ERROR: Report.fetchTenantHealthStats");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoading(false);
        }
    }

    // Load the total audit records on demand.
    async function fetchTotalAuditRecordsOnDemand() {
        if (!user?.tenantId || loadingTotalAuditRecords || totalAuditRecordsLoaded) {
            return;
        }

        setLoadingTotalAuditRecords(true);

        try {
            // Heavier on-demand total counts load.
            const response = await getHealthStatsByTenantId(user.tenantId, 11, user);
            if (response) {
                const spTotal = response.totalSharePointAuditRecords;
                const exchangeTotal = response.totalExchangeAuditRecords;
                const entraTotal = response.totalEntraIDAuditRecords;
                const teamsTotal = response.totalTeamsAuditRecords;
                const total = spTotal + exchangeTotal + entraTotal + teamsTotal;
                setTotalAuditRecords(total);
                setTotalSPRecords(spTotal);
                setTotalExchangeRecords(exchangeTotal);
                setTotalEntraRecords(entraTotal);
                setTotalTeamsRecords(teamsTotal);
                setTotalAuditRecordsLoaded(true);
            }
        }
        catch (e) {
            console.log("ERROR: Report.fetchTotalAuditRecordsOnDemand");
            console.log(e);
            setServiceIsDownError(true);
        }
        finally {
            setLoadingTotalAuditRecords(false);
        }
    }


    useEffect(() => {

        async function fetchCompanyData() {
            setLoading(true);
            try {

                if (!verifyUserHasTenantRolePermissions(user, ROLE_CODE_TENANTREPORTREADER)) {
                    setAccessDenied(true);
                    return;
                }

                if (user && user.companyId) {
                    const response1 = await getCompanyByCompanyId(user.companyId, setServiceIsDownError);
                    if (response1) {

                        setCompany(response1);

                        // Set the default to the first item in the list if there is any.
                        if (response1.tenantList && response1.tenantList.length > 0) {
                            console.log(response1.tenantList[0])
                            setSelectedTenantOption(response1.tenantList[0]);  // Set to the first item in the list.
                            fetchTenantHealthStats(user.tenantId);
                        }
                    }
                }
            }
            catch (e) {
                console.log("ERROR: Report.fetchCompanyData");
                console.log(e);
                setServiceIsDownError(true);
            }
            finally {
                setLoading(false);
            }
        }

        fetchCompanyData();

    }, []);

    // Component UI
    if (loading) {
        console.log("Loading Data ....");
        return (<ProgressBar message="Loading ..." loading={loading} />);
    }

    if (serviceIsDownError) {
        return (<><ServiceIsDown /></>);
    }

    if (selectedTenantOption && tenantHealthStats.status >= 1) {

        const sharePointPct = totalAuditRecords > 0 ? ((totalSPRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";
        const exchangePct = totalAuditRecords > 0 ? ((totalExchangeRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";
        const entraPct = totalAuditRecords > 0 ? ((totalEntraRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";
        const teamsPct = totalAuditRecords > 0 ? ((totalTeamsRecords / totalAuditRecords) * 100).toFixed(1) : "0.0";

        // A valid Tenant is chosen from the textfield selection drop down list.
        return (
            <>
                <CustomerPageScaffold
                    company={company}
                    selectedTenant={selectedTenantOption}
                    onTenantChange={handleTenantOptionChange}
                    openCompanyNag={openCompanyNag}
                    setOpenCompanyNag={setOpenCompanyNag}
                    breadcrumbPaths={theBreadcrumbPath}
                    sectionTitle={theSectionTitle}
                    sectionDescription={theSectionDesc}
                    isAdminPage={isAdminPage}
                />

                <Box>

                    <Grid container spacing={2} alignItems="stretch">
                        {accessDenied && (
                            <Grid item xs={12}>
                                <AccessDenied />
                            </Grid>
                        )}

                        {!accessDenied && (
                            <>
                                { /* Display links to reports by workload. */}
                                <ReportsByWorkloadBox colSpanVal={4} />

                                <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                    <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                        <AuditsByWorkloadSource
                                            myTitle="Summary of Audits by Workload Source (last 24 hours)"
                                            totalSharePointAuditRecords={tenantHealthStats.totalSharePointAuditRecordsIn24Hours}
                                            totalExchangeAuditRecords={tenantHealthStats.totalExchangeAuditRecordsIn24Hours}
                                            totalAzureAdAuditRecords={tenantHealthStats.totalEntraIDAuditRecordsIn24Hours}
                                            totalTeamsAuditRecords={tenantHealthStats.totalTeamsAuditRecordsIn24Hours}
                                        />
                                    </Paper>
                                </Grid>

                                { /* Display total audit record count on demand (lazy load).  Optimizes performance to show only on demand.  */}
                                <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                    <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                        <Box>
                                            <Typography
                                                component="div"
                                                variant="h6"
                                                sx={{ fontSize: 16, pb: 3 }}
                                            >
                                                Total Audit Records Stored
                                            </Typography>

                                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                                {!totalAuditRecordsLoaded && !loadingTotalAuditRecords && (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={fetchTotalAuditRecordsOnDemand}
                                                    >
                                                        Show total records
                                                    </Button>
                                                )}

                                                {loadingTotalAuditRecords && (
                                                    <Typography
                                                        component="div"
                                                        variant="body2"
                                                        sx={{ fontSize: 15 }}
                                                    >
                                                        Loading count...
                                                    </Typography>
                                                )}

                                                {totalAuditRecordsLoaded && !loadingTotalAuditRecords && (
                                                    <>
                                                        <Typography
                                                            component="div"
                                                            variant="h5"
                                                            sx={{ fontSize: 19, fontWeight: 800, mb: 2 }}
                                                        >
                                                            {totalAuditRecords?.toLocaleString()}
                                                        </Typography>

                                                        <Box sx={{ mt: 2, textAlign: 'left' }}>
                                                            <Box
                                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee' }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(255, 99, 132)' }} />
                                                                    <Typography variant="body2">
                                                                        SharePoint
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {totalSPRecords?.toLocaleString()} ({sharePointPct}%)
                                                                </Typography>
                                                            </Box>

                                                            <Box
                                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee' }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(54, 162, 235)' }} />
                                                                    <Typography variant="body2">
                                                                        Exchange
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {totalExchangeRecords?.toLocaleString()} ({exchangePct}%)
                                                                </Typography>
                                                            </Box>

                                                            <Box
                                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee' }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(255, 205, 86)' }} />
                                                                    <Typography variant="body2">
                                                                        Entra ID
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {totalEntraRecords?.toLocaleString()} ({entraPct}%)
                                                                </Typography>
                                                            </Box>

                                                            <Box
                                                                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.75, borderTop: '1px solid #eee', borderBottom: '1px solid #eee' }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'rgb(18, 211, 74)' }} />
                                                                    <Typography variant="body2">
                                                                        Teams
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                    {totalTeamsRecords?.toLocaleString()} ({teamsPct}%)
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>


                                { /* Display total audit record count. */}
                                <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                    <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                        <AuditGrowth
                                            tenantId={user.tenantId}
                                            periodToUse={REPORT_ON_PERIOD_1WEEK}
                                            reportType="All"
                                        />
                                    </Paper>
                                </Grid>

                                { /* Display Tenant Audit Storage Summary. */}
                                <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                    <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                        <Typography
                                            component="div"
                                            variant="h6"
                                            sx={{ fontSize: 16, pb: 1 }}
                                        >
                                            Tenant Database Storage Summary
                                        </Typography>
                                        <TenantDBStats
                                            user={user}
                                            tenant={selectedTenantOption}
                                            viewFullSize={true}
                                            showLink={false}
                                            showTenantUserCount={false}
                                        />
                                    </Paper>
                                </Grid>

                                { /* Display a link to Insights Search. */}
                                <InsightsSearchBox colSpanVal={4} workloadName={""} />

                                { /* Display a link to Diagnostic Logs.  */}
                                <Grid item xs={12} md={4} sx={{ p: 2 }}>
                                    <Paper elevation={3} sx={{ p: 2, border: '1px solid lightgray', height: '100%' }}>
                                        <Box sx={{ height: '100%' }}>
                                            <Typography
                                                component="div"
                                                variant="h6"
                                                sx={{ fontSize: 16, pb: 1 }}
                                            >
                                                Your Audit Job Diagnostic Logs
                                            </Typography>
                                            <Box
                                                sx={{
                                                    mt: 1.5,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<NotificationImportantIcon />}
                                                    onClick={() => navigate(`/Reports/DiagnosticsReport`)}
                                                    sx={{ width: { xs: '100%', sm: 'auto' }, whiteSpace: 'normal' }}
                                                >
                                                    View Last 7 Days
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </>
                        )}
                    </Grid>

                    {/* Feedback row */}
                    <Grid container>
                        <Grid item xs={12}>
                            <br />
                            Need a custom or new report?{" "}
                            Provide us with your{" "}
                            <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                                Product Feedback
                            </Link>.
                            <br />
                            * Unless otherwise specified as UTC, graphs on page are using your browser's local timezone.
                        </Grid>
                    </Grid>
                </Box>
            </>
        );
    }
    else {
        // Company does not yet have a tenant specified OR tenantHealthStats not ready.
        return (
            <>
                <CustomerPageScaffold
                    company={company}
                    showTenant={false}
                    openCompanyNag={openCompanyNag}
                    setOpenCompanyNag={setOpenCompanyNag}
                    breadcrumbPaths={theBreadcrumbPath}
                    sectionTitle={theSectionTitle}
                    sectionDescription={theSectionDesc}
                    isAdminPage={isAdminPage}
                />

                <Box>
                    <Grid container spacing={2}>
                        {/* Display if tenant is still pending. Else company is not yet finished setup or there are no valid tenants. */}
                        {tenantHealthStats.status === 0 ? (
                            <>
                                <Grid item xs={12}>
                                    <TenantSetupRequired />
                                </Grid>
                            </>
                        ) : (
                            <Grid item xs={12}>
                                <ProgressBar message="Loading ..." loading={true} />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <br />
                            Need a custom or new report?{" "}
                            Provide us with your{" "}
                            <Link onClick={() => navigate(`/Support/SupportHome`)} component="button">
                                Product Feedback
                            </Link>.
                        </Grid>
                    </Grid>
                </Box>
            </>
        );

    }
}
export default Reports;
